import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SessionDetails {
  id: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  startDate: string;
  endDate: string;
  zoneId: string;
  count: number;
  status: string;
  skills: string[];
  learningContent: any[]; // Optional field for learning content
  searchTags: {
    defaultTag: string[];
    eventCategory: string[];
    eventSubCategory: string[];
    eventUsage: string[];
    eventRegion: string[];
    eventDomain: string[];
  };
  agendaDetails: any[];
  eventId: string;
  branding?: {
    banner: string[];
    thumbnail: string[];
    promo: string[];
  };
  config?: {
    duration?: number;
    reminderConfig?: {
      duration: number;
      trigger: string;
    };
  };
}

export interface SessionDetailsState {
  sessions: SessionDetails[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

const initialState1 = {
  id: "",
  title: "",
  shortDescription: "",
  longDescription: "",
  startDate: "",
  endDate: "",
  zoneId: "",
  count: 0,
  status: "",
  skills: [],
  learningContent: [],
  searchTags: {
    defaultTag: [],
    eventCategory: [],
    eventSubCategory: [],
    eventUsage: [],
    eventRegion: [],
    eventDomain: []
  },
  agendaDetails: [],
  eventId: "",
  branding: {
    banner: [],
    thumbnail: [],
    promo: []
  },
  config: {
    duration: 0,
    reminderConfig: {
      duration: 0,
      trigger: ""
    }
  },
  isLoading: false,
  error: null,
  lastUpdated: null
};

const initialState: SessionDetailsState = {
  sessions: [],
  isLoading: false,
  error: null,
  lastUpdated: null
};

const sessionDetailsSlice = createSlice({
  name: "sessionDetails",
  initialState,
  reducers: {
    addSession: (state, action: PayloadAction<SessionDetails>) => {
      const newSession = action.payload;
      const existingIndex = state.sessions.findIndex(
        (s) => s.id === newSession.id
      );

      if (existingIndex !== -1) {
        const newSessions = state.sessions.map((session) =>
          session.id === newSession.id ? newSession : session
        );
        return {
          ...state,
          sessions: newSessions
        };
      } else {
        return {
          ...state,
          sessions: [...state.sessions, newSession]
        };
      }
    },
    removeSession: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        sessions: state.sessions.filter((s) => s.id !== action.payload)
      };
    },
    updateSession: (state, action: PayloadAction<SessionDetails>) => {
      const index = state.sessions.findIndex((s) => s.id === action.payload.id);
      if (index === -1) {
        // Session not found, don't update
        return state;
      }

      const newSessions = state.sessions.map((session, i) =>
        i === index ? { ...session, ...action.payload } : session
      );

      return {
        ...state,
        sessions: newSessions
      };
    },
    clearSessionDetails: (state) => {
      return {
        ...initialState,
        isLoading: false,
        error: null
      };
    }
  }
});

export const { clearSessionDetails, addSession, removeSession, updateSession } =
  sessionDetailsSlice.actions;

export default sessionDetailsSlice.reducer;
